<template>
  <div class="mt-tab">
    <div v-if="loading" class="ds-empty">
      <p class="ds-empty__message">Handleidingen laden…</p>
    </div>
    <div v-else-if="error" class="ds-empty">
      <p class="ds-empty__message">{{ error }}</p>
      <ds-button variant="ghost" data-qa="manuals-retry-button" @click="load">
        Opnieuw proberen
      </ds-button>
    </div>

    <template v-else>
      <div class="mt-tab__head">
        <ds-button data-qa="manuals-add-button" @click="showForm = true">
          <ds-icon name="plus" />
          Nieuwe handleiding toevoegen
        </ds-button>
      </div>

      <div v-if="manuals.length === 0" class="ds-empty">
        <h2 class="ds-empty__title">Geen handleidingen</h2>
        <p class="ds-empty__message">Voeg een handleiding toe om te beginnen.</p>
      </div>

      <div v-else class="ds-resource-list">
        <div v-for="manual in sortedManuals" :key="manual.id" class="ds-resource-card">
          <div class="ds-resource-card__info">
            <div class="ds-resource-card__title">{{ manual.title || '-' }}</div>
            <div class="ds-resource-card__meta">Volgorde: {{ manual.sortOrder }}</div>
          </div>
          <div class="ds-resource-card__actions">
            <button
              class="ds-icon-button"
              title="Openen"
              data-qa="manual-open-button"
              @click="openPdf(manual.sasUrl)"
            >
              <ds-icon name="eye" />
            </button>
            <button
              class="ds-icon-button"
              title="Verwijderen"
              data-qa="manual-delete-button"
              @click="confirmDelete(manual)"
            >
              <ds-icon name="trash" />
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Add modal -->
    <ds-dialog
      v-if="showForm"
      id="manuals-form"
      title="Nieuwe handleiding toevoegen"
      size="md"
      as="form"
      @close="cancelForm"
      @submit.prevent="saveForm"
    >
      <div class="ds-form">
        <div class="ds-form__field">
          <label for="manual-title">Titel</label>
          <input id="manual-title" v-model="form.title" type="text" class="ds-field" />
        </div>
        <div class="ds-form__field">
          <label for="manual-sort-order">Volgorde</label>
          <input
            id="manual-sort-order"
            v-model.number="form.sortOrder"
            type="number"
            class="ds-field"
          />
        </div>
        <div class="ds-form__field">
          <label for="manual-pdf">PDF bestand</label>
          <div class="ds-file-picker">
            <input
              id="manual-pdf"
              ref="fileInput"
              type="file"
              accept="application/pdf"
              class="ds-file-picker__native"
              @change="onFileChange"
            />
            <ds-button
              variant="ghost"
              size="sm"
              type="button"
              data-qa="manual-pdf-choose"
              @click="$refs.fileInput.click()"
            >
              <ds-icon name="upload" />
              PDF kiezen
            </ds-button>
            <span
              class="ds-file-picker__name"
              :class="{ 'ds-file-picker__name--empty': !form.file }"
            >
              {{ form.file ? form.file.name : 'Geen bestand gekozen' }}
            </span>
          </div>
        </div>
        <div v-if="formError" class="ds-alert ds-alert--danger">{{ formError }}</div>
      </div>
      <template #footer>
        <ds-button variant="text" type="button" data-qa="mt-form-cancel" @click="cancelForm">
          Annuleren
        </ds-button>
        <ds-button type="submit" data-qa="mt-form-save" :disabled="formSaving">
          <ds-spinner v-if="formSaving" inline />
          <ds-icon v-else name="save" />
          {{ formSaving ? 'Opslaan…' : 'Opslaan' }}
        </ds-button>
      </template>
    </ds-dialog>

    <ds-confirm-dialog
      id="manuals-delete"
      :show="!!deleteTarget"
      title="Handleiding verwijderen"
      :body="deleteConfirmText"
      confirm-label="Verwijderen"
      cancel-label="Annuleren"
      danger
      :confirm-disabled="deleting"
      @confirm="executeDelete"
      @cancel="deleteTarget = null"
    />
  </div>
</template>

<script>
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import DsIcon from '@/components/ds/atoms/dsIcon/DsIcon.vue'
import DsSpinner from '@/components/ds/atoms/dsSpinner/DsSpinner.vue'
import DsDialog from '@/components/ds/molecules/dsDialog/DsDialog.vue'
import DsConfirmDialog from '@/components/ds/molecules/dsConfirmDialog/DsConfirmDialog.vue'
import ManualsApi from '@/api/server/Manuals.api.js'

export default {
  name: 'ManualsTab',
  components: { DsButton, DsIcon, DsSpinner, DsDialog, DsConfirmDialog },
  data() {
    return {
      manuals: [],
      loading: false,
      error: null,

      showForm: false,
      form: { title: '', sortOrder: 0, file: null },
      formSaving: false,
      formError: null,

      deleteTarget: null,
      deleting: false,
    }
  },
  computed: {
    sortedManuals() {
      return [...this.manuals].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    },
    deleteConfirmText() {
      return this.deleteTarget
        ? `Weet je zeker dat je "${this.deleteTarget.title}" wilt verwijderen?`
        : ''
    },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        this.manuals = await ManualsApi.getManuals()
      } catch {
        this.error = 'Er is een fout opgetreden bij het ophalen van de handleidingen.'
      } finally {
        this.loading = false
      }
    },
    onFileChange(event) {
      this.form.file = event.target.files[0] || null
    },
    cancelForm() {
      this.showForm = false
      this.formError = null
      this.form = { title: '', sortOrder: 0, file: null }
    },
    async saveForm() {
      if (!this.form.file) {
        this.formError = 'Selecteer een PDF bestand.'
        return
      }
      this.formSaving = true
      this.formError = null
      try {
        await ManualsApi.addManual(this.form.file, this.form.title, this.form.sortOrder)
        this.showForm = false
        this.form = { title: '', sortOrder: 0, file: null }
        await this.load()
      } catch {
        this.formError = 'Er is een fout opgetreden bij het opslaan van de handleiding.'
      } finally {
        this.formSaving = false
      }
    },
    openPdf(url) {
      window.open(url, '_blank')
    },
    confirmDelete(manual) {
      this.deleteTarget = manual
    },
    async executeDelete() {
      this.deleting = true
      try {
        await ManualsApi.deleteManual(this.deleteTarget.id)
        this.deleteTarget = null
        await this.load()
      } catch {
        this.deleteTarget = null
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only. The cards, buttons, dialogs, file picker and form fields come
// from the design system (styles/ds/). See styles/ds/README.md before adding
// anything here.

.mt-tab__head {
  display: flex;
  justify-content: flex-start;
  margin-bottom: var(--ds-space-4);
}
</style>
